"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATA_MEDIA_TYPES = void 0;
exports.structuredView = structuredView;
exports.describeShape = describeShape;
exports.asRawText = asRawText;
exports.datasetView = datasetView;
var data_types_1 = require("./data.types");
Object.defineProperty(exports, "DATA_MEDIA_TYPES", { enumerable: true, get: function () { return data_types_1.DATA_MEDIA_TYPES; } });
const DELIMITERS = {
    'text/csv': ',',
    'text/tab-separated-values': '\t',
};
/**
 * Splits delimited text into rows keyed by its header, or gives up. Quoted cells and ragged rows
 * give up rather than guess: a wrong split would hand a tool rows that look right and are not.
 */
function delimitedRows(raw, delimiter) {
    if (raw.includes('"'))
        return undefined;
    const [header, ...rows] = raw
        .replace(/^\uFEFF/, '')
        .trim()
        .split(/\r?\n/);
    if (!header || rows.length === 0)
        return undefined;
    const keys = header.split(delimiter).map((key) => key.trim());
    // A blank or repeated header cell would key two columns as one, or a column as nothing.
    if (keys.some((key) => key === '') || new Set(keys).size !== keys.length)
        return undefined;
    const parsed = [];
    for (const row of rows) {
        const cells = row.split(delimiter).map((cell) => cell.trim());
        if (cells.length !== keys.length)
            return undefined;
        parsed.push(Object.fromEntries(keys.map((key, index) => [key, cells[index] ?? ''])));
    }
    return parsed;
}
function markdownTableRows(raw) {
    const [header, separator, ...rows] = raw.trim().split(/\r?\n/);
    if (header === undefined || separator === undefined || rows.length === 0)
        return undefined;
    if (![header, ...rows].every((line) => line.trim().startsWith('|')))
        return undefined;
    if (!/^\|?\s*:?-{3,}/.test(separator.trim()))
        return undefined;
    const cellsOf = (line) => line
        .trim()
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((cell) => cell.trim());
    const csv = [header, ...rows].map((line) => cellsOf(line).join('\t')).join('\n');
    return delimitedRows(csv, '\t');
}
/** The value read into fields when it can be, else `{ text }` so a tool still has something to show. */
function structuredView(raw, mediaType) {
    if (mediaType === 'application/json') {
        try {
            return JSON.parse(raw);
        }
        catch {
            return { text: raw };
        }
    }
    const delimiter = DELIMITERS[mediaType];
    const rows = delimiter
        ? delimitedRows(raw, delimiter)
        : mediaType === 'text/markdown'
            ? markdownTableRows(raw)
            : undefined;
    return rows ?? { text: raw };
}
/** A few words on a value's shape, for the key note: `kpis, weekly[13]`. */
function describeShape(value) {
    if (typeof value === 'string')
        return `text, ${String(value.length)} chars`;
    if (Array.isArray(value))
        return `[${String(value.length)}]`;
    if (value === null || typeof value !== 'object')
        return typeof value;
    const entries = Object.entries(value);
    const [only] = entries;
    if (entries.length === 1 && only?.[0] === 'text' && typeof only[1] === 'string') {
        return describeShape(only[1]);
    }
    return entries
        .map(([key, field]) => (Array.isArray(field) ? `${key}[${String(field.length)}]` : key))
        .join(', ');
}
/** A tool's output as text to keep, or undefined for one that cannot be written down. */
function asRawText(output) {
    if (typeof output === 'string')
        return { raw: output, mediaType: 'text/plain' };
    if (output instanceof Uint8Array || output instanceof ArrayBuffer)
        return undefined;
    try {
        // `undefined` for a function or a symbol, whatever the type says.
        const raw = JSON.stringify(output);
        return raw === undefined ? undefined : { raw, mediaType: 'application/json' };
    }
    catch {
        return undefined;
    }
}
// A dataset is the same object for as long as its agent is, so it is read into fields once.
const datasetViews = new WeakMap();
function datasetView(dataset) {
    if (!datasetViews.has(dataset)) {
        datasetViews.set(dataset, structuredView(dataset.content, dataset.mediaType));
    }
    return datasetViews.get(dataset);
}
